import React from 'react';
import { ParsedListing, NeedDTO } from '@bb/domain/dist/types';
import { ColumnsType } from 'antd/es/table';
import { useBreakpoint } from 'hooks';
import { Table } from 'components';
import { LayoutContext } from 'contexts';
import { TablePaginationConfig, TableProps } from 'antd';
import { StyledTableWrapper } from '../../Router/pages/MyBrokeragePage/StyledComponents';

type BasicListingTableProps = {
  dataSource: ParsedListing[] | NeedDTO[];
  columns: ColumnsType<ParsedListing> | ColumnsType<NeedDTO>;
  handleRowClick: (key: string) => void;
  loading: boolean;
  heightOffset: number;
  allowSelection?: boolean;
  selectedRowKeys?: React.ReactText[];
  setSelectedRowKeys?: (keys: string[]) => void;
  isFromLiveConciergePage?: boolean;
  pagination?: false | TablePaginationConfig;
  onChange?: TableProps<ParsedListing>['onChange'];
};

export const FOOTER_HEIGHT = 67;

const BasicListingTable: React.FC<BasicListingTableProps> = ({
  dataSource,
  columns,
  handleRowClick,
  loading,
  heightOffset,
  allowSelection,
  selectedRowKeys,
  setSelectedRowKeys,
  isFromLiveConciergePage,
  pagination,
  onChange,
}) => {
  const { md } = useBreakpoint();
  const wrapper = React.useRef<HTMLDivElement>();

  const { transition } = React.useContext(LayoutContext);

  const getDataSource = () => {
    if (!loading) return dataSource;
    return Array.from(Array(15)).map((_, i) => ({ key: i }));
  };

  React.useEffect(() => {
    if (wrapper.current && loading) {
      const tableBody =
        wrapper.current.getElementsByClassName('ant-table-body')[0];
      tableBody?.scrollTo(0, 0);
    }
  }, [loading]);

  return React.useMemo(() => (
      <StyledTableWrapper
        $marginBottom={pagination ? 0 : FOOTER_HEIGHT}
        ref={wrapper}
      >
        <Table
          type="primary"
          rowSelection={
            allowSelection && {
              selectedRowKeys,
              onChange: (newKeys: string[]) => setSelectedRowKeys(newKeys),
            }
          }
          showHeader={md}
          style={{ transition: `all ${transition}s`, tableLayout: 'auto' }}
          scroll={
            !isFromLiveConciergePage
              ? {
                  x: md ? 'max-content' : '100%',
                  y: `calc(100vh - ${heightOffset}px)`,
                }
              : {
                  x: md && '950px',
                  y: `max(357px, calc(100vh - ${heightOffset}px))`,
                }
          }
          pagination={pagination ?? false}
          showSorterTooltip={false}
          onRow={({ key }) => ({
            onClick: () => handleRowClick(key),
            style: { cursor: 'pointer' },
          })}
          dataSource={getDataSource()}
          columns={columns}
          onChange={onChange}
          {...(isFromLiveConciergePage && {
            hideShadow: true,
          })}
        />
      </StyledTableWrapper>
    ), [dataSource, columns, loading, selectedRowKeys, heightOffset, md]);
};

export default BasicListingTable;
